import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { History, FileSpreadsheet } from 'lucide-react';
import { StockLocationBar } from '../../components/StockLocationBar';
import {
  resolveStockScopes, effectiveBranchesOf, CENTRAL_LOCATION,
  stockHistoryOf, findCatalogProduct, variantLabelOf, subscribe,
} from '../../data/store';
import * as XLSX from 'xlsx';
export const InventoryHistoryTab = ({ org, showNotification, lockedBranchId }) => {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const unsub = subscribe(() => setVersion(v => v + 1));
    return unsub;
  }, []);

  const scopes = useMemo(() => resolveStockScopes(org.id), [org.id]);
  const branches = useMemo(() => effectiveBranchesOf(org.id), [org.id]);

  const resolveLocation = () => {
    if (scopes.inventoryScope === 'central') return CENTRAL_LOCATION;
    if (lockedBranchId) return lockedBranchId;
    return branches[0]?.id || '';
  };
  const [locationId, setLocationId] = useState(resolveLocation);

  useEffect(() => {
    setLocationId(resolveLocation());
  }, [scopes.inventoryScope, branches, lockedBranchId]);

  const accent = org.theme.primary;

  const downloadHistoryExcel = () => {
    const history = stockHistoryOf(org.id, locationId);
    if (!history || history.length === 0) {
      if (showNotification) showNotification('No history records to download.', 'warning');
      return;
    }

    const data = history.map(h => {
      const p = findCatalogProduct(h.productId);
      const dateObj = new Date(h.createdAt);
      return {
        'Date': dateObj.toLocaleDateString(),
        'Time': dateObj.toLocaleTimeString(),
        'Product Name': p ? p.name : 'Unknown',
        'Variant': variantLabelOf(p, h.variantId) || 'Standard',
        'Quantity Moved': h.qty,
        'Reason': h.reason || ''
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "History");
    XLSX.writeFile(wb, `inventory_move_history_${Date.now()}.xlsx`);
  };

  const historyRows = useMemo(() => {
    return stockHistoryOf(org.id, locationId)
      .filter(h => (h.reason || '').toLowerCase() !== 'manual pending entry')
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [org.id, locationId, version]);

  return (
    <div className="flex flex-col gap-4">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 items-center gap-4">
            <div className="flex items-center gap-2">
              <History size={18} className="text-[#0d0d0d]" />
              <h3 className="text-base font-extrabold text-[#0d0d0d]">Movement History</h3>
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <StockLocationBar
              org={org}
              scope={scopes.inventoryScope}
              mode={scopes.mode}
              branches={branches}
              locationId={locationId}
              onChange={setLocationId}
              accent={accent}
              lockedBranchId={lockedBranchId}
            />
            <button type="button" onClick={downloadHistoryExcel} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600 hover:bg-[var(--theme-primary)] hover:border-[var(--theme-primary)] hover:text-white transition-colors shadow-sm" title="Download Excel">
              <FileSpreadsheet size={14} aria-hidden="true" />
              <span className="hidden sm:inline">Export Excel</span>
            </button>
          </div>
        </div>
      </section>

      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden min-h-[400px]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left table-fixed border-collapse">
            <thead className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 border-b border-[var(--theme-tertiary)] bg-gray-50/50">
              <tr>
                <th className="px-5 py-4 w-[15%]">Date</th>
                <th className="px-3 py-4 w-[10%]">Time</th>
                <th className="px-3 py-4 w-[30%]">Product</th>
                <th className="px-3 py-4 w-[15%]">Variant</th>
                <th className="px-3 py-4 text-right w-[10%]">Qty</th>
                <th className="px-5 py-4 text-right w-[20%]">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--theme-tertiary)]">
              {historyRows.map(h => {
                const p = findCatalogProduct(h.productId);
                const pName = p ? p.name : 'Unknown Product';
                const vName = variantLabelOf(p, h.variantId);
                const dateObj = new Date(h.createdAt);
                const reasonStr = (h.reason || '').toLowerCase();
                const isAdded = reasonStr === 'moved to active' || reasonStr.startsWith('from ') || reasonStr === 'emergency product';
                const isPendingTransfer = !isAdded && h.reason;

                return (
                  <tr key={h.id} className="hover:bg-slate-50 group transition-colors">
                    <td className="px-5 py-4 text-[12px] font-bold text-slate-700">
                      {dateObj.toLocaleDateString()}
                    </td>
                    <td className="px-3 py-4 text-[12px] font-medium text-slate-500">
                      {dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-3 py-4 text-[13px] font-semibold text-[#0d0d0d]">
                      {pName}
                    </td>
                    <td className="px-3 py-4 text-[12px] font-medium text-slate-500">
                      {vName || '-'}
                    </td>
                    <td className={`px-3 py-4 text-[13px] font-bold text-right ${isAdded ? 'text-emerald-600' : isPendingTransfer ? 'text-orange-500' : 'text-[#0d0d0d]'}`}>
                      {isAdded ? '+' : isPendingTransfer ? '-' : '+'}{Math.abs(h.qty)}
                    </td>
                    <td className="px-5 py-4 text-[12px] font-semibold text-slate-600 text-right" title={h.reason}>
                      {isAdded ? (
                        <span className="text-emerald-700 text-[10px] font-bold uppercase tracking-widest">
                          {h.reason}
                        </span>
                      ) : isPendingTransfer ? (
                        <span className="text-orange-600 text-[10px] font-bold uppercase tracking-widest">
                          {h.reason}
                        </span>
                      ) : (
                        <span className="text-slate-600 text-[10px] font-bold uppercase tracking-widest">
                          {h.reason}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {historyRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-16 text-center text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
                    <div className="flex flex-col items-center justify-center">
                      <History size={24} className="text-slate-300 mb-2" />
                      <p>No history records found.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

InventoryHistoryTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
  lockedBranchId: PropTypes.string,
};
